import React, { useState } from "react";
import styled from "styled-components";
import theme from "../theme";
import { Dialog } from "@mui/material";

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.8);
  z-index: 999;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ModalBox = styled.div`
  background: white;
  border-radius: 10px;
  max-width: 1000px;
  max-height: 90vh;
  display: flex;
  position: relative;
  overflow: hidden;
  padding: 1rem;
  width: auto;

  ${theme.breakpoints.down("sm")} {
    flex-direction: column;
  }
`;

const LeftColumn = styled.div`
  width: 30%;
  max-width: 12rem;
  padding: 0.5rem;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  gap: 0.5rem;
  /* hauteur fixe pour forcer le scroll interne sur desktop */
  height: 70vh;
  max-height: 70vh;
  overflow-y: auto;
  overflow-x: hidden;

  img {
    border-radius: 8px;
    margin-bottom: 1rem;
    max-width: 12rem;
    max-height: 7rem;
    background: transparent;
    cursor: pointer;
    transition: transform 0.2s;
    padding: 0.5rem;
  }

  img:hover {
    transform: scale(1.05);
  }

  ${theme.breakpoints.down("sm")} {
    margin-top: 3rem;
    width: 100%;
    max-width: 100%;
    flex-direction: row;
    justify-content: flex-start;
    gap: 0.5rem;
    padding: 0.5rem;
    height: auto;
    max-height: none;
    overflow-x: auto;
    overflow-y: hidden;
  }
`;

const RightColumn = styled.div`
  width: 70%;
  padding: 1rem;
  overflow-y: auto;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  ${theme.breakpoints.down("sm")} {
    width: auto;
    padding: 0.5rem;
  }
`;

const Title = styled.h2`
  margin-top: 0;
  margin-bottom: 0.5rem;
  color: var(--my-color3);
  font-size: 1.4rem;
`;

const Description = styled.p`
  margin-top: 1rem;
  margin-bottom: 0.5rem;
  font-size: 1rem;
  line-height: 1.7;
  color: #444;
`;

const FeatureList = styled.ul`
  margin: 0rem;
  padding: 0rem;
  padding-left: 0.5rem;
  list-style: none;
  background-color: #f9f9f9;
  padding: 1rem;
  border-radius: 10px;
  padding-bottom: 1px;

  li {
    margin-bottom: 1rem;
    line-height: 1.7;
    font-size: 0.95rem;
    color: #333;
  }
`;

const FeatureTitle = styled.span`
  font-weight: 700;
  color: var(--my-color3);
  display: inline;
  margin-right: 0.3rem;
`;

const FeatureText = styled.span`
  font-weight: 400;
  color: #555;
  display: inline;
`;

const TechnologiesSection = styled.div`
  padding-top: 0.5rem;
  margin: 0rem;
`;

const TechnologiesTitle = styled.h4`
  margin: 0 0 0.8rem 0;
  font-size: 0.9rem;
  color: var(--my-color3);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const TechnologiesContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const TechnologyBadge = styled.span`
  display: inline-block;
  padding: 0.4rem 0.8rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 500;
  background: rgba(var(--my-color5-rgb), 0.3);
  color: var(--my-color3);
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }
`;

const LinkButton = styled.a`
  display: inline-block;
  background: var(--my-color3);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  text-decoration: none;
  font-weight: bold;
  margin-top: 1rem;
  width: fit-content;

  &:hover {
    background: var(--light);
    color: var(--my-color3);
  }

  ${theme.breakpoints.down("sm")} {
    display: block;
    margin: auto;
    margin-top: 2rem;
  }
`;

const CloseBtn = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: var(--my-color3);
  color: white;
  border: none;
  padding: 0.5rem;
  border-radius: 6px;
  cursor: pointer;
`;

const FullImage = styled.img`
  max-width: 90vw;
  max-height: 90vh;
  border-radius: 10px;
`;

const ProjectModal = ({ project, onClose }) => {
  const [selectedImage, setSelectedImage] = useState(null);

  const handleImageClick = (img) => {
    setSelectedImage(img);
  };

  const handleImageClose = () => {
    setSelectedImage(null);
  };

  return (
    <>
      <Dialog open={project !== null} onClick={onClose}>
        <ModalBox onClick={(e) => e.stopPropagation()}>
          <LeftColumn>
            {project.carousel.map((img, i) => (
              <img
                key={i}
                src={img}
                alt={`${project.title} slide ${i}`}
                onClick={() => handleImageClick(img)}
              />
            ))}
          </LeftColumn>
          <RightColumn>
            <div>
              <Title>{project.title}</Title>
              {project.description && (
                <Description>{project.description}</Description>
              )}
              {project.features && project.features.length > 0 && (
                <FeatureList>
                  {project.features.map((feat, i) => {
                    // Support both old string format and new {title, text} format
                    if (typeof feat === "string") {
                      return <li key={i}>{feat}</li>;
                    }
                    return (
                      <li key={i}>
                        <FeatureTitle>{feat.title} :</FeatureTitle>
                        <FeatureText> {feat.text}</FeatureText>
                      </li>
                    );
                  })}
                </FeatureList>
              )}
              {project.technologies && project.technologies.length > 0 && (
                <TechnologiesSection>
                  <TechnologiesContainer>
                    {project.technologies.map((tech, i) => (
                      <TechnologyBadge key={i}>{tech}</TechnologyBadge>
                    ))}
                  </TechnologiesContainer>
                </TechnologiesSection>
              )}
              {project.link && (
                <div
                  style={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "flex-end",
                  }}
                >
                  <LinkButton
                    href={project.link}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Visiter le site
                  </LinkButton>
                </div>
              )}
            </div>
          </RightColumn>
          <CloseBtn onClick={onClose}>Fermer</CloseBtn>
        </ModalBox>
      </Dialog>

      {/* Fenêtre d'image agrandie */}
      <Dialog open={selectedImage !== null} onClose={handleImageClose}>
        <ModalOverlay onClick={handleImageClose}>
          <FullImage
            src={selectedImage}
            alt="Zoom"
            onClick={(e) => e.stopPropagation()}
          />
        </ModalOverlay>
      </Dialog>
    </>
  );
};

export default ProjectModal;
